import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type WebinarRegion } from '@/utils/onboardingExperience';
import { useCallback, useEffect, useRef } from 'react';

interface UseBookDemoCalendarAnalyticsParams {
  countryCode: string | undefined;
  language: string | null;
  meetingUrl: string | null;
}

export function useBookDemoCalendarAnalytics({
  countryCode,
  language,
  meetingUrl,
}: UseBookDemoCalendarAnalyticsParams) {
  const hasTrackedUnsupportedRegion = useRef(false);

  useEffect(() => {
    // Reset the guard whenever the country becomes supported or clears, so a
    // fresh unsupported country later still emits exactly one event.
    if (!countryCode || meetingUrl) {
      hasTrackedUnsupportedRegion.current = false;
      return;
    }
    if (hasTrackedUnsupportedRegion.current) return;
    hasTrackedUnsupportedRegion.current = true;
    track({
      event_name: TrackingEventName.EarlyMeetingRegionNotSupported,
      props: { country_code: countryCode, language: language ?? undefined },
    });
  }, [countryCode, meetingUrl, language]);

  const trackLanguageSelected = useCallback((selectedLanguage: string) => {
    track({
      event_name: TrackingEventName.LanguageSelected,
      props: { language: selectedLanguage, surface: 'slg_flow_demo' },
    });
  }, []);

  const trackSlotSelected = useCallback(
    (durationMs: number) => {
      track({
        event_name: TrackingEventName.MeetingSlotSelected,
        props: {
          source: 'early_meeting_flow',
          // HubSpot's meeting API returns durations in milliseconds; the analytics
          // schema expects minutes, so convert here.
          duration_minutes: Math.round(durationMs / 60_000),
          country_code: countryCode,
          language: language ?? undefined,
        },
      });
    },
    [countryCode, language]
  );

  const trackBookingSubmitted = useCallback(() => {
    track({
      event_name: TrackingEventName.MeetingBookingSubmitted,
      props: {
        source: 'early_meeting_flow',
        country_code: countryCode,
        language: language ?? undefined,
      },
    });
  }, [countryCode, language]);

  const trackBooked = useCallback(
    (slotStartIso: string) => {
      track({
        event_name: TrackingEventName.MeetingBooked,
        props: {
          source: 'early_meeting_flow',
          slot_start_iso: slotStartIso,
          country_code: countryCode,
          language: language ?? undefined,
        },
      });
    },
    [countryCode, language]
  );

  const trackBookingFailed = useCallback(
    (errorType: string | undefined) => {
      track({
        event_name: TrackingEventName.MeetingBookingFailed,
        props: {
          source: 'early_meeting_flow',
          error_type: errorType,
          country_code: countryCode,
          language: language ?? undefined,
        },
      });
    },
    [countryCode, language]
  );

  const trackSdrDemoRequested = useCallback(
    (countryName: string | undefined) => {
      track({
        event_name: TrackingEventName.SdrDemoRequested,
        props: {
          has_reason: true,
          country: countryName,
          country_code: countryCode,
        },
      });
    },
    [countryCode]
  );

  const trackWebinarSubmitted = useCallback(
    (region: WebinarRegion, timeslotLabel: string) => {
      track({
        event_name: TrackingEventName.WebinarRegistrationSubmitted,
        props: { timeslot_label: timeslotLabel, region },
      });
    },
    []
  );

  const bookWithAnalytics = useCallback(
    async (
      slotStartMillisUtc: number,
      invokeBooking: () => Promise<void>
    ): Promise<{ ok: true } | { ok: false; error: unknown }> => {
      trackBookingSubmitted();
      try {
        await invokeBooking();
        trackBooked(new Date(slotStartMillisUtc).toISOString());
        return { ok: true };
      } catch (error) {
        trackBookingFailed(error instanceof Error ? error.name : undefined);
        return { ok: false, error };
      }
    },
    [trackBookingSubmitted, trackBooked, trackBookingFailed]
  );

  return {
    trackLanguageSelected,
    trackSlotSelected,
    trackBookingSubmitted,
    trackBooked,
    trackBookingFailed,
    trackSdrDemoRequested,
    trackWebinarSubmitted,
    bookWithAnalytics,
  };
}
